import { backendApi } from "@/services/backend/api";
import { updateCache } from "@/services/backend/_lib/update-cache";
import {
  ReferenceNumberFilters,
  ResetReferenceNumberRequest,
} from "@/services/backend/documents/types/reference-number";
import { LastMinuteRiskAnalysis } from "@/services/backend/rlt/work-orders/types/last-minute-risk-analysis";
import { Filter } from "@/shared/lib/filter/filter";
import {
  DocumentBaseFilters,
  DocumentCommonFilters,
} from "@/services/backend/documents/types/filters";
import { OrdrBy } from "@/shared/lib/ordr/ordrBy";
import { DocumentState } from "@/services/backend/documents/types/state";

const lmraApi = backendApi.injectEndpoints({
  endpoints: (build) => ({
    rltLMRACreate: build.mutation<LastMinuteRiskAnalysis, CreateLMRARequest>({
      query: (request) => ({
        url: `/rlt/lmra`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: ["RltLMRA"],
    }),
    rltLMRAShow: build.query<LastMinuteRiskAnalysis, ShowLMRARequest>({
      query: (request) => ({
        url: `/rlt/lmra`,
        method: "GET",
        params: request,
      }),
      providesTags: (result) =>
        result ? [{ type: "RltLMRA", id: result.id }] : [],
    }),
    rltLMRAPatch: build.mutation<LastMinuteRiskAnalysis, PatchRequest>({
      query: (request) => ({
        url: `/rlt/lmra`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "RltLMRA", id: request.id },
        { type: "RltLMRA", id: "LIST" },
      ],
    }),
    rltLMRAResetReferenceNumber: build.mutation<
      LastMinuteRiskAnalysis,
      ResetReferenceNumberRequest
    >({
      query: (request) => ({
        url: `/rlt/lmra/reset-reference-number`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltLMRA", id: request.id },
        { type: "RltLMRA", id: "LIST" },
      ],
    }),
    rltLMRAList: build.query<List, ListRequest>({
      query: (request) => ({
        url: `/rlt/lmra/list`,
        method: "GET",
        params: {
          request: btoa(JSON.stringify(request)),
        },
      }),
      providesTags: (result) =>
        result ? [{ type: "RltLMRA", id: "LIST" }] : [],
    }),
    rltLMRASetCheckResult: build.mutation<
      LastMinuteRiskAnalysis,
      SetCheckResult
    >({
      query: (request) => ({
        url: `/rlt/lmra/check/result`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltLMRA", id: request.id },
        "RltLMRA",
      ],
    }),
    rltLMRASetCheckSelection: build.mutation<
      LastMinuteRiskAnalysis,
      SetCheckSelection
    >({
      query: (request) => ({
        url: `/rlt/lmra/check/selection`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltLMRA", id: request.id },
        "RltLMRA",
      ],
    }),
    rltLMRACommentCheck: build.mutation<
      LastMinuteRiskAnalysis,
      CommentCheckRequest
    >({
      query: (request) => ({
        url: `/rlt/lmra/check/comment`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltLMRA", id: request.id },
        "RltLMRA",
      ],
    }),
    rltLMRAConfirm: build.mutation<LastMinuteRiskAnalysis, ConfirmLMRARequest>({
      query: (request) => ({
        url: `/rlt/lmra/confirm`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltLMRA", id: request.id },
        "RltLMRA",
      ],
    }),
    rltLMRAUnconfirm: build.mutation<
      LastMinuteRiskAnalysis,
      UnconfirmLMRARequest
    >({
      query: (request) => ({
        url: `/rlt/lmra/unconfirm`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltLMRA", id: request.id },
        "RltLMRA",
      ],
    }),
    rltLMRADelete: build.mutation<LastMinuteRiskAnalysis, DeleteLMRARequest>({
      query: (request) => ({
        url: `/rlt/lmra`,
        method: "DELETE",
        params: request,
      }),
      invalidatesTags: ["RltLMRA"],
    }),
  }),
});

export const {
  useRltLMRACreateMutation,
  useRltLMRAShowQuery,
  useRltLMRAPatchMutation,
  useRltLMRAResetReferenceNumberMutation,
  useRltLMRAListQuery,
  useRltLMRASetCheckResultMutation,
  useRltLMRASetCheckSelectionMutation,
  useRltLMRACommentCheckMutation,
  useRltLMRAConfirmMutation,
  useRltLMRAUnconfirmMutation,
  useRltLMRADeleteMutation,
} = lmraApi;

// PatchRequest updates the document. All fields except `id` are optional:
// an omitted key leaves the corresponding value unchanged.
export interface PatchRequest {
  id: string;
  referenceNumber?: string;
}

export interface CreateLMRARequest {
  id: string;
  processId: string;
  companyId: string;
  branchId: string;
  customerId: string;
  serviceObjectId: string;
}

export interface ShowLMRARequest {
  id: string;
}

export interface List {
  lmras: LastMinuteRiskAnalysis[];
  count: number;
}

export interface ListRequest
  extends DocumentBaseFilters, DocumentCommonFilters, ReferenceNumberFilters {
  orderBy?: OrdrBy<string>[];
  confirmedAt?: Filter<string>;
  state?: Filter<DocumentState>;
  limit?: number;
  offset?: number;
}

export interface SetCheckResult {
  id: string;
  checkId: string;
  result: null | number;
}

export interface SetCheckSelection {
  id: string;
  checkId: string;
  selected: number[];
}

export interface CommentCheckRequest {
  id: string;
  checkId: string;
  comment: string;
}

export interface CheckResultItem {
  checkId: string;
  result?: number;
  selected?: number[];
  comment?: string;
}

export interface ConfirmLMRARequest {
  id: string;
  conductedBy: string;
  confirmedBy: string;
  checkResults: CheckResultItem[];
}

export interface UnconfirmLMRARequest {
  id: string;
}

export interface DeleteLMRARequest {
  id: string;
}

export const updateRltLMRACache = updateCache(lmraApi, "rltLMRAShow");
